import type { BuildingDto, IssueCreate, IssueDetail, IssueStatus } from "@klantpunt/shared";
import { STATUS } from "@klantpunt/shared";
import { isVideoFile, queuePhoto } from "../lib/photos";
import { applyOverlay, localIssue, type Overlay } from "./ops";
import { queue } from "./queue";

/**
 * Wijzigingen die ook zonder verbinding kunnen (SPEC §11): nieuwe bevinding, foto, opmerking, status.
 * Ze lopen altijd via de wachtrij; online komt het antwoord van de server meteen terug,
 * offline een lokale versie (`queued: true`) die na synchroniseren wordt vervangen.
 */
export interface Me {
  id: string;
  name: string;
}
export type Result = { issue: IssueDetail; queued: boolean };

export async function createIssue(
  b: Pick<BuildingDto, "id" | "code">,
  body: Omit<IssueCreate, "locations" | "photos"> & {
    locations: { floorId: string; roomId: string | null; x: number | null; y: number | null }[];
    photos: string[];
  },
  me: Me,
): Promise<Result> {
  const local = localIssue(
    b,
    {
      ...body,
      description: body.description ?? null,
      installationId: body.installationId ?? null,
      elementCode: body.elementCode ?? null,
    },
    me,
  );
  const r = await queue.enqueue<IssueDetail>({
    id: body.id,
    buildingId: b.id,
    label: "Nieuwe bevinding",
    req: { kind: "http", method: "POST", path: `/buildings/${b.id}/issues`, body },
    overlay: { t: "create", issue: local },
  });
  return r.status === "done" ? { issue: r.data, queued: false } : { issue: local, queued: true };
}

async function change(
  d: IssueDetail,
  buildingId: string,
  label: string,
  req: { method: "POST" | "PATCH"; path: string; body: unknown },
  overlay: Overlay,
): Promise<Result> {
  const r = await queue.enqueue<IssueDetail>({ buildingId, label, req: { kind: "http", ...req }, overlay });
  return r.status === "done" ? { issue: r.data, queued: false } : { issue: applyOverlay(d, overlay), queued: true };
}

export const changeStatus = (d: IssueDetail, buildingId: string, status: IssueStatus) =>
  change(
    d,
    buildingId,
    `Status van ${d.code} → ${STATUS.find((s) => s.k === status)?.l ?? status}`,
    { method: "PATCH", path: `/issues/${d.id}`, body: { status } },
    { t: "status", issueId: d.id, status },
  );

export const addComment = (d: IssueDetail, buildingId: string, body: string, me: Me) =>
  change(
    d,
    buildingId,
    `Opmerking bij ${d.code}`,
    { method: "POST", path: `/issues/${d.id}/comments`, body: { body } },
    {
      t: "comment",
      issueId: d.id,
      event: { id: crypto.randomUUID(), kind: "comment", body, user: me, createdAt: new Date().toISOString() },
    },
  );

export async function addPhoto(d: IssueDetail, buildingId: string, file: File): Promise<Result> {
  const fileId = await queuePhoto(file, buildingId);
  const video = isVideoFile(file);
  return change(
    d,
    buildingId,
    `${video ? "Video" : "Foto"} bij ${d.code}`,
    { method: "POST", path: `/issues/${d.id}/photos`, body: { fileId } },
    { t: "photo", issueId: d.id, fileId, mime: video ? "video/*" : undefined },
  );
}
